import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Icon, { type IconName } from './Icon.js';

type Variant = 'primary' | 'secondary' | 'plain' | 'destructive';
type Size = 'sm' | 'md' | 'lg';

function cls(variant: Variant, size: Size, block: boolean, extra?: string): string {
  return `ui-btn ui-btn--${variant} ui-btn--${size}${block ? ' ui-btn--block' : ''}${extra ? ` ${extra}` : ''}`;
}

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  block = false,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  icon?: IconName;
  block?: boolean;
  children?: ReactNode;
}): JSX.Element {
  return (
    <button type={type} className={cls(variant, size, block, className)} {...rest}>
      {icon && <Icon name={icon} size={size === 'lg' ? 22 : 18} />}
      {children}
    </button>
  );
}

export function ButtonLink({
  to,
  variant = 'primary',
  size = 'md',
  icon,
  block = false,
  className,
  children,
}: {
  to: string;
  variant?: Variant;
  size?: Size;
  icon?: IconName;
  block?: boolean;
  className?: string;
  children?: ReactNode;
}): JSX.Element {
  return (
    <Link to={to} className={cls(variant, size, block, className)}>
      {icon && <Icon name={icon} size={18} />}
      {children}
    </Link>
  );
}

/** A 44px round icon-only button. `label` is its accessible name. */
export function IconButton({
  icon,
  label,
  onClick,
  size = 22,
  className,
  active = false,
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
  size?: number;
  className?: string;
  active?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`ui-iconbtn${active ? ' ui-iconbtn--on' : ''}${className ? ` ${className}` : ''}`}
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      <Icon name={icon} size={size} />
    </button>
  );
}
